import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Copy, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { paymentMethods } from "@/lib/weddingData";
import HandHeart from "./HandHeart";

const CopyableField = ({ label, value, copyLabel, testId }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value.replace(/\s+/g, ""));
      setCopied(true);
      toast.success(`✓ ${copyLabel} copiado`);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error(`No pudimos copiar el ${copyLabel}. Prueba a mano.`);
    }
  };

  return (
    <div>
      <dt className="corner-label text-[#8a8580]">{label}</dt>
      <dd className="mt-2 flex flex-wrap items-center gap-4">
        <span
          className="font-mono text-base tracking-wider md:text-lg"
          data-testid={`${testId}-value`}
        >
          {value}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          aria-label={`Copiar ${copyLabel}`}
          data-testid={`${testId}-copy`}
          className="btn-base btn-outline"
        >
          {copied ? (
            <Check strokeWidth={1.5} className="h-4 w-4" />
          ) : (
            <Copy strokeWidth={1.5} className="h-4 w-4" />
          )}
          <span>{copied ? `${copyLabel} copiado` : `Copiar ${copyLabel}`}</span>
        </button>
      </dd>
    </div>
  );
};

const BankTransferCard = ({ method, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      className="border border-[#c9c3b3] bg-[#f2ede2]"
      data-testid={`bank-card-${method.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`bank-card-toggle-${method.id}`}
        className="flex w-full items-center justify-between gap-4 px-6 py-6 text-left transition-colors duration-300 hover:bg-[#ece7dc] md:px-8"
      >
        <span>
          <span className="corner-label text-[#8a8580]">
            {method.label}
          </span>
          <span
            className="mt-1 block display-md leading-tight"
            data-testid={`${method.id}-holder`}
          >
            {method.accountHolder}
          </span>
        </span>
        <span className="shrink-0" aria-hidden="true">
          {open ? (
            <Minus strokeWidth={1.5} className="h-5 w-5" />
          ) : (
            <Plus strokeWidth={1.5} className="h-5 w-5" />
          )}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <dl className="space-y-6 border-t border-[#c9c3b3] px-6 py-6 md:px-8 md:py-8">
              {method.iban ? (
                <CopyableField
                  label="IBAN"
                  value={method.iban}
                  copyLabel="IBAN"
                  testId={`${method.id}-iban`}
                />
              ) : null}

              {method.accountNumber ? (
                <CopyableField
                  label={method.accountNumberLabel || "Nº de cuenta"}
                  value={method.accountNumber}
                  copyLabel="cuenta"
                  testId={`${method.id}-account`}
                />
              ) : null}

              {method.cci ? (
                <CopyableField
                  label="CCI Interbancario"
                  value={method.cci}
                  copyLabel="CCI"
                  testId={`${method.id}-cci`}
                />
              ) : null}

              {method.bic ? (
                <div>
                  <dt className="corner-label text-[#8a8580]">BIC / SWIFT</dt>
                  <dd
                    className="mt-2 font-mono text-lg tracking-wider"
                    data-testid={`${method.id}-bic`}
                  >
                    {method.bic}
                  </dd>
                </div>
              ) : null}
            </dl>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const GiftList = () => {
  return (
    <section
      id="gifts"
      data-testid="gifts-section"
      className="border-t border-[#c9c3b3] py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex items-start justify-between">
          <p className="corner-label text-[#8a8580]">(07) — Regalos</p>
        </div>

        <div className="mt-16 grid grid-cols-12 gap-6 md:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-7 relative"
          >
            <HandHeart
              variant={2}
              size={40}
              rotate={10}
              className="absolute -left-2 -top-6 hidden md:block"
            />
            <h2 className="display-xl">
              Un pequeño
              <br />
              <span className="italic-serif">detalle.</span>
            </h2>
          </motion.div>
          <div className="col-span-12 md:col-span-5 md:pt-8">
            <p className="body-lead max-w-md">
              Vuestra presencia es el mejor regalo que podríamos recibir. Si,
              además, os apetece contribuir a nuestra próxima aventura, os
              dejamos nuestros datos bancarios por aquí. Mil gracias por formar
              parte de este día tan especial.{" "}
              <HandHeart
                variant={3}
                size={22}
                rotate={-6}
                className="inline-block align-middle -mt-1"
              />
            </p>
          </div>
        </div>

        {/* Payment methods — collapsible cards, stacked vertically for a cleaner section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mx-auto mt-16 flex max-w-3xl flex-col gap-4"
        >
          {paymentMethods.map((method) =>
            method.type === "bank_transfer" ? (
              <BankTransferCard key={method.id} method={method} />
            ) : null
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default GiftList;
